// What publishing a review changes besides the review: the attention
// hand-off ("I'm done, it's the author's turn") and the reviewers asked to
// look again. Pure: the Review view shows these plans and PrStore applies
// them once the review is in (see `PrStore.publishWithHandOff`).
import type { PullRequestData } from '../api/queries';
import type { AttentionMember } from '../engine/types';
import { hasMember, sameLogin, type AttentionChange } from './attention';
import { isBot } from './timeline';

/** Who the hand-off is about, and which overrides the viewer may write (`PrStore.canSetAttention`). */
export interface HandOffPeople {
  viewer: string;
  author: string;
  /** The attention set now. */
  members: readonly AttentionMember[];
  canSet: (login: string) => boolean;
}

/**
 * The viewer's edits to the default hand-off: logins whose default change
 * is turned off, and people added by hand. No edits is the default
 * hand-off, which is on.
 */
export interface HandOffEdits {
  readonly off: readonly string[];
  readonly extra: readonly string[];
}

export const NO_EDITS: HandOffEdits = { off: [], extra: [] };

/**
 * One change the hand-off shows: the viewer's removal and the author's
 * addition can be turned off (`on`); someone added by hand is taken out of
 * `extra` instead.
 */
export type HandOffItem =
  | { login: string; change: 'remove'; role: 'you'; on: boolean }
  | { login: string; change: 'add'; role: 'author'; on: boolean }
  | { login: string; change: 'add'; role: 'other' };

export interface HandOff {
  items: HandOffItem[];
  /** Why the default hand-off leaves someone out. */
  notes: string[];
  /** What publishing applies: exactly the items shown on, the viewer's removal first. */
  changes: AttentionChange[];
}

const NO_RIGHTS = 'needs triage access to the repository or authorship of the pull request';

/**
 * The hand-off for `people` with `edits`: remove the viewer when they are
 * waiting, and add the author unless the viewer is the author, the author
 * is already waiting, or the viewer may not add them; then each person
 * added by hand whom the viewer may add and who is not waiting or in it
 * already.
 */
export function handOffPlan(people: HandOffPeople, edits: HandOffEdits): HandOff {
  const { viewer, author, members, canSet } = people;
  const off = (login: string) => edits.off.some((o) => sameLogin(o, login));
  const items: HandOffItem[] = [];
  const notes: string[] = [];
  if (viewer && hasMember(members, viewer) && canSet(viewer)) items.push({ login: viewer, change: 'remove', role: 'you', on: !off(viewer) });
  if (author && sameLogin(author, viewer)) notes.push("You're the author, so there is nobody to hand back to.");
  else if (author && hasMember(members, author)) notes.push(`${author} is already waiting.`);
  else if (author && !canSet(author)) notes.push(`Adding ${author} ${NO_RIGHTS}.`);
  else if (author) items.push({ login: author, change: 'add', role: 'author', on: !off(author) });
  for (const login of edits.extra) {
    if (!login || hasMember(members, login) || !canSet(login)) continue;
    if (sameLogin(login, viewer) || items.some((i) => sameLogin(i.login, login))) continue;
    items.push({ login, change: 'add', role: 'other' });
  }
  const changes = items.filter((i) => i.role === 'other' || i.on).map((i) => ({ login: i.login, state: i.change }));
  return { items, notes, changes };
}

/** A reviewer's standing: their latest review's state, or `REQUESTED` while a review is requested of them. */
export type ReviewerState = 'APPROVED' | 'CHANGES_REQUESTED' | 'COMMENTED' | 'DISMISSED' | 'REQUESTED';

export interface ReviewerRow {
  /** A login, or a team's name. */
  name: string;
  state: ReviewerState;
  /** A past reviewer with no request pending: one may ask them to review again. */
  reRequest: boolean;
  team?: true;
}

/**
 * The pull request's reviewers besides the viewer and the author: those who
 * reviewed (latest state), then those asked who have not yet, teams last.
 * A pending request wins over a past review.
 */
export function reviewersOf(pr: Pick<PullRequestData, 'latestReviews' | 'reviewRequests'>, viewer: string, author: string): ReviewerRow[] {
  const skip = (login: string) => sameLogin(login, viewer) || sameLogin(login, author);
  const requested = pr.reviewRequests.nodes.flatMap((n) => (n.requestedReviewer?.__typename === 'User' ? [n.requestedReviewer.login] : []));
  const isRequested = (login: string) => requested.some((r) => sameLogin(r, login));
  const rows: ReviewerRow[] = [];
  for (const r of pr.latestReviews.nodes) {
    const login = r.author?.login;
    if (!login || isBot(r.author) || skip(login) || rows.some((x) => sameLogin(x.name, login))) continue;
    rows.push(isRequested(login) ? { name: login, state: 'REQUESTED', reRequest: false } : { name: login, state: r.state as ReviewerState, reRequest: true });
  }
  for (const login of requested)
    if (!skip(login) && !rows.some((x) => sameLogin(x.name, login))) rows.push({ name: login, state: 'REQUESTED', reRequest: false });
  for (const n of pr.reviewRequests.nodes)
    if (n.requestedReviewer?.__typename === 'Team') rows.push({ name: n.requestedReviewer.name, state: 'REQUESTED', reRequest: false, team: true });
  return rows;
}
